// Protocolo de payload dentro del plaintext cifrado E2E.
//
// Cada mensaje se cifra E2E como un string. Distinguimos los formatos
// usando un primer carácter de control invisible:
//
//   - sin prefijo                -> texto plano (usuario escribe normal)
//   - prefijo \x01 + JSON        -> payload visible: media, contacto, mensaje de grupo, texto con reply
//   - prefijo \x02 + JSON        -> payload de control: read receipt, group_invite, profile, edit
//   - prefijo \x03 + JSON        -> self-copy: copia cifrada para el emisor de un mensaje que envió

export const VISIBLE_MARKER = String.fromCharCode(1);
export const CONTROL_MARKER = String.fromCharCode(2);
export const SELF_COPY_MARKER = String.fromCharCode(3);

export interface ReplyContext {
  id: string;
  preview: string;
  from: string;
}

export interface MediaPayload {
  kind: 'image' | 'video' | 'audio' | 'file';
  media_id: string;
  file_key: string;
  name: string;
  size: number;
  content_type: string;
  group_id?: string;
  reply_to?: ReplyContext;
}

export interface ContactPayload {
  kind: 'contact';
  syphrax_id: string;
  group_id?: string;
  reply_to?: ReplyContext;
}

export interface GroupTextPayload {
  kind: 'group_text';
  group_id: string;
  text: string;
  reply_to?: ReplyContext;
}

export interface TextReplyPayload {
  kind: 'text_reply';
  text: string;
  reply_to: ReplyContext;
  group_id?: string;
}

export type VisiblePayload = MediaPayload | ContactPayload | GroupTextPayload | TextReplyPayload;

// v016 — receipts agregados por device.
// El receiver envia uno por cada (messageId, deviceId). El sender mantiene
// Sets per-message: |delivered_devices|>=1 -> 2 aspas grises;
// |read_devices|>=1 -> 2 aspas iluminadas.
// device_id opcional para compat con ctrls pre-v111 que no lo enviaban.
export interface ReadReceipt {
  kind: 'read';
  message_id: string;
  device_id?: string;
}

export interface DeliveredReceipt {
  kind: 'delivered';
  message_id: string;
  device_id?: string;
}

export interface GroupInvite {
  kind: 'group_invite';
  group_id: string;
  group_name: string;
  members: Array<{ syphrax_id: string; public_key: string }>;
  freq_name: string | null;
}

export interface ProfilePayload {
  kind: 'profile';
  display_name: string;
  photo_b64: string | null;
  info: string;
  freq_name: string | null;
}

export interface EditPayload {
  kind: 'edit';
  message_id: string;
  text: string;
  group_id?: string;
}

export interface ReactionPayload {
  kind: 'reaction';
  message_id: string;
  emoji: string | null; // null = quitar mi reacción
  group_id?: string;
}

export interface CallOfferPayload {
  kind: 'call_offer';
  call_id: string;
  sdp: string;
  has_video: boolean;
  freq_name: string | null;
}

export interface CallAnswerPayload {
  kind: 'call_answer';
  call_id: string;
  sdp: string;
}

export interface CallIcePayload {
  kind: 'call_ice';
  call_id: string;
  candidate: string;
  sdp_mid: string | null;
  sdp_m_line_index: number | null;
}

export interface SyncPingPayload {
  kind: 'sync_ping';
}

export interface RequestInvitePayload {
  kind: 'request_invite';
  group_id: string;
}

export interface CallEndPayload {
  kind: 'call_end';
  call_id: string;
  reason?: 'hangup' | 'rejected' | 'busy' | 'failed' | 'cancelled';
}

// Recovery automatica de desync ratchet: el peer recibe un blob ratchet sin
// initial pero no tiene state local (server reset, vault limpio, etc). Pide al
// sender que reinicie X3DH. El sender limpia su sessionKey y el proximo msg
// dispara X3DH initiator desde cero. Sin intervencion del usuario.
export interface RequestX3dhResetPayload {
  kind: 'request_x3dh_reset';
  reason?: string;
}

// Sync de historial entre devices del mismo account.
// Cuando un device nuevo se registra, los devices viejos del mismo user le
// envían su snapshot de chats/grupos/perfiles. JSON serializado del estado.
export interface HistorySyncPayload {
  kind: 'history_sync';
  payload_json: string;
  generated_at: string;
}

export type ControlPayload =
  | ReadReceipt
  | DeliveredReceipt
  | GroupInvite
  | ProfilePayload
  | EditPayload
  | ReactionPayload
  | SyncPingPayload
  | RequestInvitePayload
  | CallOfferPayload
  | CallAnswerPayload
  | CallIcePayload
  | CallEndPayload
  | RequestX3dhResetPayload
  | HistorySyncPayload;

export interface SelfCopyPayload {
  target_kind: 'contact' | 'group';
  target_id: string;
  group_id?: string;
  inner: string;
  message_id: string;
  at: string;
}

export type DecodedPayload =
  | { type: 'text'; text: string }
  | { type: 'visible'; payload: VisiblePayload }
  | { type: 'control'; payload: ControlPayload }
  | { type: 'self_copy'; payload: SelfCopyPayload }
  | { type: 'unknown'; raw: string };

export function encodeVisible(p: VisiblePayload): string {
  return VISIBLE_MARKER + JSON.stringify(p);
}

export function encodeControl(p: ControlPayload): string {
  return CONTROL_MARKER + JSON.stringify(p);
}

export function encodeSelfCopy(p: SelfCopyPayload): string {
  return SELF_COPY_MARKER + JSON.stringify(p);
}

export function decodePayload(plain: string): DecodedPayload {
  if (plain.length === 0) return { type: 'text', text: '' };
  const first = plain.charCodeAt(0);
  if (first === 1) {
    try {
      return { type: 'visible', payload: JSON.parse(plain.slice(1)) as VisiblePayload };
    } catch {
      return { type: 'unknown', raw: plain };
    }
  }
  if (first === 2) {
    try {
      return { type: 'control', payload: JSON.parse(plain.slice(1)) as ControlPayload };
    } catch {
      return { type: 'unknown', raw: plain };
    }
  }
  if (first === 3) {
    try {
      return { type: 'self_copy', payload: JSON.parse(plain.slice(1)) as SelfCopyPayload };
    } catch {
      return { type: 'unknown', raw: plain };
    }
  }
  return { type: 'text', text: plain };
}
